import { api } from "./client";
import { ApiError } from "./errors";
import type {
  CompleteUploadResult,
  InitiatedImageUpload,
  ProcessingJob,
  ProductImage,
} from "./types";

/** Constants transcribed from the backend, not guessed.
 *
 * app/services/draft_image_service.py:
 *   SUPPORTED_CONTENT_TYPES, MAX_IMAGE_BYTES, MIN_IMAGE_BYTES
 * The declared checksum is compared against hashlib.sha256(data).hexdigest(),
 * so it must be lowercase SHA-256 hex.
 */
export const SUPPORTED_CONTENT_TYPES = [
  "image/avif",
  "image/webp",
  "image/png",
  "image/jpeg",
] as const;

export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
export const MIN_IMAGE_BYTES = 64;

/** The three original views a colour's source set is made of, mapped to the
 *  derivative role the backend accepts for replacing each one.
 *
 *  Replacing any of these makes the backend rebuild the colour from its whole
 *  source set, so the UI must never describe it as swapping one output file. */
export const SOURCE_VIEWS = [
  {
    view: "front",
    label: "Front",
    imageRole: "gallery_front",
    previewRole: "gallery_front",
    regenerates: "the listing card, the front gallery image and the thumbnail",
  },
  {
    view: "side",
    label: "Side",
    imageRole: "gallery_side",
    previewRole: "gallery_side",
    regenerates: "the side gallery image",
  },
  {
    view: "angle",
    label: "Angle",
    imageRole: "gallery_angle",
    previewRole: "gallery_angle",
    regenerates: "the angle gallery image",
  },
] as const;

export type SourceView = (typeof SOURCE_VIEWS)[number]["view"];

/**
 * The source photographs of a family that is not photographed front/side/angle.
 *
 * These are SOURCE names, not derivative ones, and the difference is the whole
 * reason this list is separate from SOURCE_VIEWS above. A frame's listing card
 * is composed from its front view, so "replace the listing card" is a request
 * an administrator can mean and `gallery_front` is a real translation of it. A
 * contact lens has no front: `photo_1` IS the photograph. Sending it as
 * `gallery_front` would be a row claiming a frame's front view was supplied,
 * and the backend now stores these three literally so it does not have to be.
 *
 * Roles are sent exactly as written here. Nothing maps them to front, side,
 * angle or any gallery role on the way out — see `uploadable_roles` in
 * app/services/product_family.py, which refuses the frame vocabulary for these
 * families anyway.
 */
export const GENERIC_PHOTO_SLOTS = [
  {
    role: "photo_1",
    label: "Main photograph",
    required: true,
    hint: "Shown on the product card and as the first image.",
    // Which derivative to show back as this source's preview. From
    // _IMAGE_ROLES_BY_PHOTO_ROLE in app/services/product_family.py, not guessed.
    previewRole: "listing_card",
    regenerates: "the product card and the first gallery image",
  },
  {
    role: "photo_2",
    label: "Second photograph",
    required: false,
    hint: "Optional. Another view of the same product.",
    previewRole: "gallery_side",
    regenerates: "the second gallery image",
  },
  {
    role: "photo_3",
    label: "Third photograph",
    required: false,
    hint: "Optional. Another view of the same product.",
    previewRole: "gallery_angle",
    regenerates: "the third gallery image",
  },
] as const;

export type GenericPhotoRole = (typeof GENERIC_PHOTO_SLOTS)[number]["role"];

export const GENERIC_PHOTO_ROLES: readonly GenericPhotoRole[] =
  GENERIC_PHOTO_SLOTS.map((slot) => slot.role);

/** One source photograph an administrator can replace, in either vocabulary. */
export interface SourceSlot {
  /** The role sent to initiate-upload. Sent literally, never translated. */
  imageRole: string;
  label: string;
  /** The derivative shown back as this source's preview. */
  previewRole: string;
  regenerates: string;
}

/**
 * The source photographs of a family, in the family's own vocabulary.
 *
 * The single place that answers it, so no screen has to decide for itself
 * whether a product is described by front/side/angle or by photo_1/2/3. A
 * frame's three views are DERIVATIVE names it replaces through, and the other
 * families' three are the source names themselves — different questions with
 * the same shape, which is exactly why one function answers both.
 */
export function sourceSlotsForFamily(family: string | null): readonly SourceSlot[] {
  if (family === "glasses") {
    return SOURCE_VIEWS.map((view) => ({
      imageRole: view.imageRole,
      label: view.label,
      previewRole: view.previewRole,
      regenerates: view.regenerates,
    }));
  }
  return GENERIC_PHOTO_SLOTS.map((slot) => ({
    imageRole: slot.role,
    label: slot.label,
    previewRole: slot.previewRole,
    regenerates: slot.regenerates,
  }));
}

/** SHA-256 hex of the file, computed in the browser.
 *
 * The backend verifies the uploaded bytes against this, which is what turns a
 * truncated or corrupted upload into a clean rejection instead of a product
 * built from a damaged photograph. Returns null where Web Crypto is
 * unavailable, because the field is optional and a wrong value is worse than
 * an absent one.
 */
export async function sha256Hex(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export const imagesApi = {
  initiateUpload: (
    productId: string,
    body: {
      expected_version: number;
      image_role: string;
      variant_id: string;
      original_filename: string;
      content_type: string;
      file_size: number;
      checksum?: string;
    },
  ) =>
    api.post<InitiatedImageUpload>(
      `/admin/products/${productId}/draft/images/initiate-upload`,
      body,
    ),

  completeUpload: (
    productId: string,
    body: { expected_version: number; draft_image_upload_id: string },
  ) =>
    api.post<CompleteUploadResult>(
      `/admin/products/${productId}/draft/images/complete-upload`,
      body,
    ),

  /**
   * Finish several already-initiated uploads as ONE first source set.
   *
   * For a frame that has never been photographed. Its listing card is composed
   * from front, side and angle together, so there is no earlier set to carry
   * the other two roles forward from and completing one upload at a time is
   * refused. The set is the unit of work.
   *
   * Session ids only. No storage key, no filename, no role and no colour: the
   * backend already recorded every one of those against the session it opened,
   * and sending a second copy would be asking it to trust a claim instead.
   */
  completeInitialSet: (
    productId: string,
    body: { expected_version: number; draft_image_upload_ids: string[] },
  ) =>
    api.post<CompleteUploadResult>(
      `/admin/products/${productId}/draft/images/complete-initial-set`,
      body,
    ),

  processingJob: (jobId: string, signal?: AbortSignal) =>
    api.get<ProcessingJob>(`/admin/processing-jobs/${jobId}`, undefined, signal),

  updateImage: (
    productId: string,
    imageId: string,
    body: { expected_version: number; enabled?: boolean; display_order?: number },
  ) =>
    api.patch<{ image: ProductImage }>(
      `/admin/products/${productId}/draft/images/${imageId}`,
      body,
    ),

  removeImage: (productId: string, imageId: string, expectedVersion: number) =>
    api.del<{ removed: boolean; image_id: string; storage_object_retained: boolean }>(
      `/admin/products/${productId}/draft/images/${imageId}`,
      { expected_version: expectedVersion },
    ),

  reorderImages: (
    productId: string,
    body: { expected_version: number; ordering: string[] },
  ) =>
    api.post<{ images: ProductImage[] }>(
      `/admin/products/${productId}/draft/images/reorder`,
      body,
    ),
};

/**
 * PUT the file straight to object storage.
 *
 * Deliberately not routed through the shared api client: this request must
 * carry NO admin cookie and NO CSRF header. The signed URL already authorises
 * exactly this one object, and attaching session credentials to a third-party
 * origin would leak them beyond the backend.
 *
 * The URL is never included in the thrown error. A signed URL in a message is a
 * signed URL in a screenshot, a log or a support ticket.
 */
export async function putToSignedUrl(
  signed: Pick<InitiatedImageUpload, "upload_url" | "method" | "headers">,
  file: File,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(signed.upload_url, {
      method: signed.method,
      headers: signed.headers,
      body: file,
      credentials: "omit",
    });
  } catch {
    throw new ApiError(
      "network",
      0,
      "the file could not be sent to storage",
      null,
    );
  }
  if (!response.ok) {
    throw new ApiError(
      "server",
      response.status,
      "storage refused the upload",
      null,
    );
  }
}

